'use strict';
import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LangContext } from '../App';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

// ── Translations ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Nouveau signalement',
    sousTitre:
      'Signalez du braconnage, une intrusion ou un comportement suspect. Votre signalement peut rester anonyme.',
    retour: '← Mes signalements',

    labelType: 'Type de signalement',
    typeRequis: '(requis)',
    types: {
      braconnage: 'Braconnage',
      tir_hors_zone: 'Tir hors zone',
      intrusion: 'Intrusion',
      comportement_anormal: 'Comportement anormal',
      autre: 'Autre',
    },

    labelDescription: 'Description',
    descriptionAide: 'Décrivez ce que vous avez observé (minimum 10 caractères).',
    descriptionPh:
      "Ex. : J'ai vu un véhicule traverser le sentier balisé après la fermeture de la zone.",

    labelObservedAt: "Date d'observation (optionnel)",
    observedAtAide: "Quand l'incident s'est-il produit ?",

    labelLocalisation: 'Localisation GPS',
    localisationAide: 'Cliquez sur «Utiliser ma position» ou entrez les coordonnées manuellement.',
    utiliserPosition: 'Utiliser ma position GPS',
    lat: 'Latitude',
    lng: 'Longitude',

    labelAnonyme: 'Soumettre anonymement',
    anonymeAide:
      'Si activé, votre identité ne sera pas conservée. La traçabilité publique sera absente.',
    anonymeWarning:
      "⚠ Ce signalement ne sera PAS conservé sous votre nom et n'apparaîtra pas dans votre historique « Mes signalements ». Les autorités le recevront mais vous ne pourrez pas le consulter par la suite.",

    soumettre: 'Soumettre le signalement',
    enCours: 'Soumission en cours…',
    succes: 'Signalement reçu. Numéro de référence',
    succesAnonyme: 'Signalement anonyme reçu',
    refAnonymeAide:
      "Conservez ce numéro : c'est votre seule trace de cette soumission. Le signalement n'apparaîtra pas dans «Mes signalements».",
    voirMesSignalements: 'Voir mes signalements',
    retourTableau: 'Retour au tableau de bord',

    erreurRequis: 'Veuillez remplir les champs requis.',
    erreurDescription: 'La description doit contenir au moins 10 caractères.',
    erreurGeoloc: "Impossible d'obtenir votre position GPS.",
    erreurLatLng: 'Coordonnées GPS invalides.',
    erreurServeur: 'Erreur lors de la soumission. Réessayez.',
  },
  en: {
    title: 'New Report',
    sousTitre: 'Report poaching, trespassing or suspicious behaviour. You can submit anonymously.',
    retour: '← My reports',

    labelType: 'Report type',
    typeRequis: '(required)',
    types: {
      braconnage: 'Poaching',
      tir_hors_zone: 'Out-of-zone shooting',
      intrusion: 'Trespassing',
      comportement_anormal: 'Abnormal behaviour',
      autre: 'Other',
    },

    labelDescription: 'Description',
    descriptionAide: 'Describe what you observed (minimum 10 characters).',
    descriptionPh: 'e.g. I saw a vehicle cross the marked trail after the zone was closed.',

    labelObservedAt: 'Date observed (optional)',
    observedAtAide: 'When did the incident take place?',

    labelLocalisation: 'GPS location',
    localisationAide: 'Click "Use my GPS" or enter coordinates manually.',
    utiliserPosition: 'Use my GPS',
    lat: 'Latitude',
    lng: 'Longitude',

    labelAnonyme: 'Submit anonymously',
    anonymeAide: 'If enabled, your identity is not stored. Public traceability is absent.',
    anonymeWarning:
      '⚠ This report will NOT be stored under your name and will NOT appear in your "My Reports" history. Authorities will receive it but you won\'t be able to look it up later.',

    soumettre: 'Submit report',
    enCours: 'Submitting…',
    succes: 'Report received. Reference number',
    succesAnonyme: 'Anonymous report received',
    refAnonymeAide:
      'Keep this number — it is your only trace of this submission. The report will not appear in "My Reports".',
    voirMesSignalements: 'View my reports',
    retourTableau: 'Back to dashboard',

    erreurRequis: 'Please fill required fields.',
    erreurDescription: 'Description must be at least 10 characters long.',
    erreurGeoloc: 'Could not obtain your GPS position.',
    erreurLatLng: 'Invalid GPS coordinates.',
    erreurServeur: 'Submission failed. Try again.',
  },
};

// ── Style helpers (match the project's dark theme) ────────────────────────────

const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  color: 'rgba(255,255,255,0.55)',
  marginBottom: '0.35rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 6,
  color: 'var(--couleur-texte)',
  padding: '0.6rem 0.85rem',
  fontSize: '0.92rem',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const aideStyle = {
  display: 'block',
  fontSize: '0.75rem',
  color: 'rgba(255,255,255,0.40)',
  marginTop: '0.3rem',
};

const sectionStyle = {
  marginBottom: '1.5rem',
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function SubmitReport() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    report_type: '',
    description: '',
    observed_at: '',
    latitude: '',
    longitude: '',
    anonyme: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [erreur, setErreur] = useState('');
  const [success, setSuccess] = useState(null); // { id, anonyme }

  const set = (field) => (e) => {
    const value = e?.target?.type === 'checkbox' ? e.target.checked : (e?.target?.value ?? e);
    setForm((f) => ({ ...f, [field]: value }));
  };

  const usePosition = () => {
    if (!navigator.geolocation) {
      setErreur(t.erreurGeoloc);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setForm((f) => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        })),
      () => setErreur(t.erreurGeoloc),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setErreur('');

    if (!form.report_type || !form.description || !form.latitude || !form.longitude) {
      setErreur(t.erreurRequis);
      return;
    }
    if (form.description.trim().length < 10) {
      setErreur(t.erreurDescription);
      return;
    }
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (
      Number.isNaN(lat) ||
      Number.isNaN(lng) ||
      lat < -90 ||
      lat > 90 ||
      lng < -180 ||
      lng > 180
    ) {
      setErreur(t.erreurLatLng);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        report_type: form.report_type,
        description: form.description.trim(),
        latitude: lat,
        longitude: lng,
        anonyme: form.anonyme,
      };
      if (form.observed_at) {
        payload.observed_at = new Date(form.observed_at).toISOString();
      }
      const rep = await api.post('/reports', payload);
      setSuccess({ id: rep.data?.id, anonyme: form.anonyme });
    } catch (err) {
      setErreur(err.response?.data?.error || err.response?.data?.erreurs?.[0] || t.erreurServeur);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <PrivateLayout>
        <main style={{ maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' }}>
          <div
            style={{
              background: 'rgba(22,163,74,0.10)',
              border: '1px solid rgba(22,163,74,0.40)',
              borderRadius: 'var(--rayon)',
              padding: '2rem',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✓</div>
            <h2 style={{ color: 'var(--couleur-or)', marginBottom: '1rem' }}>
              {success.anonyme ? t.succesAnonyme : t.succes}
            </h2>
            <code style={{ fontSize: '0.85rem', color: 'var(--couleur-texte)' }}>{success.id}</code>
            {success.anonyme && (
              <p
                style={{ marginTop: '1rem', color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem' }}
              >
                {t.refAnonymeAide}
              </p>
            )}
            <div style={{ marginTop: '1.5rem' }}>
              {success.anonyme ? (
                <Link
                  to="/dashboard"
                  style={{
                    display: 'inline-block',
                    padding: '.55rem 1.2rem',
                    background: 'var(--couleur-or)',
                    color: '#000',
                    borderRadius: 8,
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t.retourTableau} →
                </Link>
              ) : (
                <Link
                  to="/my-reports"
                  style={{
                    display: 'inline-block',
                    padding: '.55rem 1.2rem',
                    background: 'var(--couleur-or)',
                    color: '#000',
                    borderRadius: 8,
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t.voirMesSignalements} →
                </Link>
              )}
            </div>
          </div>
        </main>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <main style={{ maxWidth: 700, margin: '0 auto', padding: '2rem 1rem' }}>
        <Link
          to="/my-reports"
          style={{
            display: 'inline-block',
            marginBottom: '0.7rem',
            color: 'rgba(255,255,255,0.5)',
            textDecoration: 'none',
            fontSize: '0.85rem',
          }}
        >
          {t.retour}
        </Link>
        <PageHeader icon="🚨" title={t.title} subtitle={t.sousTitre} />

        <form onSubmit={submit}>
          <div style={sectionStyle}>
            <label style={labelStyle}>
              {t.labelType} <span style={{ color: 'var(--couleur-or)' }}>{t.typeRequis}</span>
            </label>
            <select
              value={form.report_type}
              onChange={set('report_type')}
              style={inputStyle}
              required
            >
              <option value="">—</option>
              {Object.entries(t.types).map(([val, label]) => (
                <option key={val} value={val}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>
              {t.labelDescription}{' '}
              <span style={{ color: 'var(--couleur-or)' }}>{t.typeRequis}</span>
            </label>
            <textarea
              value={form.description}
              onChange={set('description')}
              style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
              placeholder={t.descriptionPh}
              required
              minLength={10}
            />
            <span style={aideStyle}>{t.descriptionAide}</span>
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>{t.labelObservedAt}</label>
            <input
              type="datetime-local"
              value={form.observed_at}
              onChange={set('observed_at')}
              style={inputStyle}
            />
            <span style={aideStyle}>{t.observedAtAide}</span>
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>
              {t.labelLocalisation}{' '}
              <span style={{ color: 'var(--couleur-or)' }}>{t.typeRequis}</span>
            </label>
            <button
              type="button"
              onClick={usePosition}
              style={{
                ...inputStyle,
                background: 'rgba(201,162,39,0.15)',
                border: '1px solid rgba(201,162,39,0.45)',
                color: 'var(--couleur-or)',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '0.6rem',
                textAlign: 'left',
              }}
            >
              📍 {t.utiliserPosition}
            </button>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <div>
                <input
                  type="number"
                  step="any"
                  min="-90"
                  max="90"
                  value={form.latitude}
                  onChange={set('latitude')}
                  placeholder={t.lat}
                  style={inputStyle}
                  required
                />
              </div>
              <div>
                <input
                  type="number"
                  step="any"
                  min="-180"
                  max="180"
                  value={form.longitude}
                  onChange={set('longitude')}
                  placeholder={t.lng}
                  style={inputStyle}
                  required
                />
              </div>
            </div>
            <span style={aideStyle}>{t.localisationAide}</span>
          </div>

          <div style={sectionStyle}>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.6rem',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={form.anonyme}
                onChange={set('anonyme')}
                style={{ marginTop: '0.2rem' }}
              />
              <span>
                <span style={{ color: 'var(--couleur-texte)' }}>{t.labelAnonyme}</span>
                <span style={aideStyle}>{t.anonymeAide}</span>
              </span>
            </label>
            {form.anonyme && (
              <div
                style={{
                  marginTop: '0.6rem',
                  padding: '0.7rem 1rem',
                  background: 'rgba(217,119,6,0.10)',
                  border: '1px solid rgba(217,119,6,0.40)',
                  borderRadius: 6,
                  color: '#fcd34d',
                  fontSize: '0.85rem',
                  lineHeight: 1.45,
                }}
              >
                {t.anonymeWarning}
              </div>
            )}
          </div>

          {erreur && (
            <div
              style={{
                background: 'rgba(220,38,38,0.10)',
                border: '1px solid rgba(220,38,38,0.40)',
                borderRadius: 6,
                padding: '0.7rem 1rem',
                color: '#fca5a5',
                marginBottom: '1rem',
                fontSize: '0.9rem',
              }}
            >
              {erreur}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '0.85rem',
              background: submitting ? 'rgba(201,162,39,0.5)' : 'var(--couleur-or)',
              color: '#000',
              border: 'none',
              borderRadius: 8,
              fontWeight: 700,
              fontSize: '0.95rem',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? t.enCours : t.soumettre}
          </button>
        </form>
      </main>
    </PrivateLayout>
  );
}
